/**
 * @skydio/mission-logic
 *
 * Shared mission editing logic for Skydio Cloud and Mobile platforms.
 *
 * This package contains:
 * - Constraints: Min/max/default values for mission parameters
 * - Validators: Validation functions for mission data
 * - Defaults: Functions to create default mission objects
 *
 * @packageDocumentation
 */

// Export constraints
export {
  WAYPOINT_SPEED,
  RETURN_SPEED,
  WAYPOINT_HEIGHT,
  WAYPOINT_HEADING,
  WAYPOINT_GIMBAL_PITCH,
  WAYPOINT_PAUSE_TIME,
  MAP_CAPTURE_GIMBAL,
  MAP_CAPTURE_DIAGONAL_GIMBAL,
  MAP_CAPTURE_PERIMETER_GIMBAL,
  BUFFER_TIME_FOR_MISSION_SEC,
  MAP_CAPTURE_OVERLAP,
  MAP_CAPTURE_SIDELAP,
  MAP_CAPTURE_HEIGHT,
  MAP_CAPTURE_SPEED,
  MAP_CAPTURE_SCAN_PLANE_OFFSET,
  MAP_CAPTURE_FLIGHT_DIRECTION,
  MAP_CAPTURE_DIAGONAL_OVERLAP,
  MAP_CAPTURE_PERIMETER_OVERLAP,
  VEHICLE_SPEED_LIMITS,
  VERTICAL_SPEED,
  GIMBAL_PITCH_RADIANS,
  RETURN_HEIGHT,
  TERRAIN_FOLLOWING_HEIGHT,
  AUTONOMOUS_FLIGHT,
  KML_IMPORT_LIMITS,
  RETURN_TO_LAUNCH,
  ASSET_HEIGHT,
  getWaypointSpeedConstraints,
  getReturnSpeedConstraints,
  isX10,
  isX2,
  isS2,
  hasVT300Z,
  hasVT300L,
  hasV100L,
  isUnknown,
  hasThermal,
  hasRjpeg,
  humanizeVehicleConfiguration,
  type VehicleType,
  type VehicleConfiguration,
  type FlightMode,
} from "./constraints";

// Export validators
export {
  validateMissionDetails,
  validateWaypoint,
  validateReturnBehavior,
  validateMission,
  isMissionValid,
  type MissionDetails,
  type WaypointData,
  type ReturnBehaviorData,
  type MissionValidationResult,
} from "./validators";

// Export defaults
export {
  createDefaultMissionDetails,
  createDefaultWaypointData,
  createDefaultReturnBehavior,
  createDefaultMission,
  clamp,
  clampWaypoint,
  clampReturnBehavior,
  type MissionData,
} from "./defaults";

// Export capture settings options & constraints
export {
  getWhiteBalanceOptions,
  formatWhiteBalance,
  DEFAULT_WHITE_BALANCE,
  getShutterSpeedOptions,
  formatShutterSpeed,
  DEFAULT_SHUTTER_SPEED,
  getIsoOptions,
  formatIso,
  DEFAULT_ISO,
  getExposureCompensationOptions,
  formatExposureCompensation,
  DEFAULT_EXPOSURE_COMPENSATION,
  isExposureCompensationDisabled,
  getCameraModeOptions,
  getResolutionOptions,
  recordingModeToResolution,
  resolutionToRecordingMode,
  getAvailableCameraModes,
  PHOTO_FORMAT_OPTIONS,
  THERMAL_FORMAT_OPTIONS,
  thermalFormatsToRjpegEnabled,
  rjpegEnabledToThermalFormats,
  getZoomConstraints,
  DEFAULT_ZOOM,
  PhotoResolution,
  type CameraModeOption,
  type ResolutionOption,
  type PhotoFormatOption,
  type ThermalFormatOption,
  type ZoomConstraints,
} from "./captureSettings";

// Export video settings
export {
  VideoResolution,
  VideoAspectRatio,
  VideoMode,
  X10_VIDEO_RECORDING_MODE_MAP,
  getX10VideoRecordingMode,
  parseX10VideoRecordingMode,
  X10_VIDEO_RESOLUTION_OPTIONS,
  X10_VIDEO_ASPECT_RATIO_OPTIONS,
  X10_VIDEO_MODE_OPTIONS,
  DEFAULT_X10_VIDEO_MODE,
  LegacyVideoFps,
  LegacyVideoMode,
  LEGACY_VIDEO_RECORDING_MODE_MAP,
  getLegacyVideoRecordingMode,
  parseLegacyVideoRecordingMode,
  LEGACY_1080P_VALID_FPS,
  LEGACY_VIDEO_RESOLUTION_OPTIONS,
  LEGACY_VIDEO_FPS_OPTIONS,
  LEGACY_VIDEO_MODE_OPTIONS,
  DEFAULT_LEGACY_VIDEO_MODE,
  getAvailableLegacyFpsOptions,
  parseVideoRecordingMode,
  BITS_IN_MEGABITS,
  CUSTOM_BITRATE_RANGE,
  CUSTOM_BITRATE_MIN,
  CUSTOM_BITRATE_MAX,
  CUSTOM_BITRATE_DEFAULT,
  formatBitrate,
  isCustomBitrateAvailable,
  type X10ParsedVideoMode,
  type LegacyParsedVideoMode,
} from "./videoSettings";

// Export interval settings
export {
  X10_INTERVAL_VALUES,
  X2S2_INTERVAL_VALUES,
  formatInterval,
  getIntervalOptions,
  getIntervalValues,
  type IntervalOption,
} from "./intervalSettings";

// Export photo settings migration
export {
  migratePhotoSettings,
  getPhotoSettingsBasePath,
  type MigratablePhotoAction,
  type MigratedPhotoFormatSettings,
} from "./migratePhotoSettings";

// Export sensor mappings
export { SensorPackage, SensorPackageToThermal, SensorPackageToEoSensors } from "./sensorMappings";

// Export action tree utilities
export * from "./actionTree";

// Export mission store utilities
export * from "./missionStore";

// Export waypoint utilities
export {
  extractWaypointValues,
  cameraActionToPickerIndex,
  pickerIndexToCameraAction,
  CameraActionPickerIndex,
  type WaypointValues,
  type CameraActionType,
  type InTransitCameraActionType,
} from "./waypoint-utils";

// Export simulation utilities
export {
  simulateMissionExecution,
  simulateAction,
  poiMissionFilter,
  distanceFromLatLng,
  getWaypointXYGeodetic,
  trackAnalytic,
  setTrackAnalytic,
  setElevationProbe,
  getElevationProbe,
  type SimulationResult,
  type ActionEstimate,
  type ActionEstimateWithId,
  type ElevationProbe,
} from "./simLite";
